/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/
  
import { HTMLAttributes, useState, useEffect } from 'preact/compat';
import { Container } from '@adobe-commerce/elsie/lib';
import { Pagination as PaginationComponent } from '@adobe-commerce/elsie/components';
import { events } from '@adobe-commerce/event-bus';
import { Scope, SearchVariables } from '@/plp/data/models';
import { search } from '@/plp/api';

export interface PaginationProps extends HTMLAttributes<HTMLDivElement> {
  scope?: Scope;
  onPageChange?: (page: number) => void;
 }
    
export const Pagination: Container<PaginationProps> = ({ scope, onPageChange }) => {
  const [currentPage, setCurrentPage] = useState<number>();
  const [totalPages, setTotalPages] = useState<number>();
  const [currentSearchVariables, setCurrentSearchVariables] = useState<SearchVariables>({});

  useEffect(() => {
    const searchEvent = events.on('search/result', (payload) => {
      setCurrentSearchVariables(payload.request);
      setCurrentPage(payload.result?.pageInfo?.currentPage);
      setTotalPages(payload.result?.pageInfo?.totalPages);
    }, { eager: true, scope });

    return () => {
      searchEvent?.off();
    };
  }, []);

  const handlePageChange = async (page: number): Promise<void> => {
    onPageChange?.(page);

    search(
      { ...currentSearchVariables, currentPage: page },
      { scope }
    );
  };
  

  // Don't render if we don't have the variables
  if (!currentPage || !totalPages) {
    return null;
  }

  return (
    <PaginationComponent 
      currentPage={currentPage}
      totalPages={totalPages}
      onChange={handlePageChange}
    />
  );
};